/**
 * Product comparison: "Compare" buttons (snippets/compare-toggle), the tray (snippets/compare-tray), the
 * comparison tool (sections/compare-tool, /search?view=compare) and the behaviour of every comparison
 * table (snippets/compare-table): only-differences filter, collapsible groups, two columns on phones.
 *
 * The selection (2–4 products) is kept in localStorage, so it survives browsing; without storage it lives
 * for the page only. The tool loads each product's column from the server (sections/compare-column, the same
 * verified records as the product pages), so no product data is duplicated here.
 */
(() => {
  const cfg = window.theme?.compare;
  if (!cfg) return;
  const MAX = 4;
  const KEY = 'bh-compare-v1';
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  let memory = [];

  const read = () => {
    try {
      const list = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(list) ? list.filter((x) => x && typeof x.h === 'string').slice(0, MAX) : [];
    } catch (e) {
      return memory;
    }
  };
  const write = (list) => {
    memory = list.slice(0, MAX);
    try { localStorage.setItem(KEY, JSON.stringify(memory)); } catch (e) { /* page-only selection */ }
    render();
  };

  const status = () => qs('[data-compare-status]');
  const announce = (msg) => {
    const el = status();
    if (!el) return;
    el.textContent = '';
    window.setTimeout(() => { el.textContent = msg; }, 50);
  };
  const fill = (str, vars) => Object.entries(vars).reduce((s, [k, v]) => s.replaceAll(`__${k}__`, v), str);

  /* ---------- Buttons and tray ---------- */
  const syncButtons = () => {
    const handles = read().map((x) => x.h);
    qsa('[data-compare-toggle]').forEach((btn) => {
      const on = handles.includes(btn.dataset.handle) ? 'true' : 'false';
      if (btn.getAttribute('aria-pressed') !== on) btn.setAttribute('aria-pressed', on);
    });
  };
  const render = () => {
    const list = read();
    syncButtons();
    const tray = qs('[data-compare-tray]');
    if (!tray) return;
    tray.hidden = list.length === 0 || Boolean(qs('[data-compare-tool]'));
    const ul = qs('[data-compare-list]', tray);
    ul.replaceChildren(...list.map((item) => {
      const li = document.createElement('li');
      li.className = 'cmp-tray__item';
      const a = document.createElement('a');
      a.href = item.u;
      a.title = item.t;
      if (item.i) {
        const img = document.createElement('img');
        img.src = item.i;
        img.alt = item.t;
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        a.append(img);
      } else {
        a.textContent = item.t;
      }
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'cmp-tray__remove';
      rm.dataset.compareRemove = item.h;
      rm.setAttribute('aria-label', fill(cfg.strings.remove, { TITLE: item.t }));
      rm.innerHTML = '<span aria-hidden="true">×</span>';
      li.append(a, rm);
      return li;
    }));
    qs('[data-compare-count]', tray).textContent = fill(cfg.strings.count, { COUNT: list.length, MAX });
    const go = qs('[data-compare-go]', tray);
    const ready = list.length >= 2;
    go.setAttribute('aria-disabled', ready ? 'false' : 'true');
    go.classList.toggle('is-disabled', !ready);
    qs('[data-compare-hint]', tray).hidden = ready;
  };

  const toggle = (btn) => {
    const list = read();
    const h = btn.dataset.handle;
    const i = list.findIndex((x) => x.h === h);
    if (i > -1) {
      list.splice(i, 1);
      write(list);
      announce(fill(cfg.strings.removed, { TITLE: btn.dataset.title }));
      return;
    }
    if (list.length >= MAX) {
      announce(cfg.strings.full);
      const tray = qs('[data-compare-tray]');
      tray?.classList.add('is-full');
      window.setTimeout(() => tray?.classList.remove('is-full'), 1200);
      return;
    }
    list.push({ h, t: btn.dataset.title, i: btn.dataset.image || '', u: btn.dataset.url });
    write(list);
    announce(fill(cfg.strings.added, { TITLE: btn.dataset.title, COUNT: list.length }));
  };

  document.addEventListener('click', (event) => {
    const t = event.target.closest('[data-compare-toggle]');
    if (t) { event.preventDefault(); toggle(t); return; }
    const rm = event.target.closest('[data-compare-remove]');
    if (rm) {
      event.preventDefault();
      write(read().filter((x) => x.h !== rm.dataset.compareRemove));
      if (qs('[data-compare-tool]')) buildTool();
      return;
    }
    if (event.target.closest('[data-compare-clear]')) {
      write([]);
      if (qs('[data-compare-tool]')) buildTool();
      return;
    }
    const go = event.target.closest('[data-compare-go]');
    if (go && go.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      announce(cfg.strings.need_two);
    }
  });
  window.addEventListener('storage', (event) => { if (event.key === KEY) render(); });

  /* ---------- Table behaviour (server tables and the tool) ---------- */
  const setupTable = (wrap) => {
    if (wrap.dataset.ready) return;
    wrap.dataset.ready = '1';
    const cols = parseInt(wrap.dataset.cols, 10) || 0;
    qsa('.cmp__group', wrap).forEach((group) => {
      group.classList.toggle('has-diff', Boolean(qs('.cmp__row.is-diff', group)));
      const title = qs('[data-cmp-group]', group);
      if (!title) return;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cmp__group-btn';
      btn.setAttribute('aria-expanded', 'true');
      btn.innerHTML = `<span>${title.textContent.trim()}</span>${qs('template[data-cmp-chevron]')?.innerHTML || ''}`;
      title.replaceWith(btn);
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        group.classList.toggle('is-collapsed', open);
      });
    });
    const diff = qs('[data-cmp-diff]', wrap);
    diff?.addEventListener('change', () => wrap.classList.toggle('cmp--diff-only', diff.checked));
    // Phones: two columns; "Change" shows the next product that is not on screen.
    qsa('[data-cmp-swap]', wrap).forEach((btn) => {
      btn.hidden = cols <= 2;
      btn.addEventListener('click', () => {
        const col = parseInt(btn.closest('[data-col]').dataset.col, 10);
        const slot = String(col) === wrap.dataset.a ? 'a' : 'b';
        const other = parseInt(wrap.dataset[slot === 'a' ? 'b' : 'a'], 10);
        let next = col;
        do { next = (next % cols) + 1; } while (next === other || next === col);
        wrap.dataset[slot] = String(next);
        const name = qs(`.cmp__head [data-col="${next}"] .cmp__name`, wrap);
        if (name) announce(fill(cfg.strings.showing, { TITLE: name.textContent.trim() }));
        qs(`.cmp__head [data-col="${next}"] [data-cmp-swap]`, wrap)?.focus();
      });
    });
  };

  /* ---------- Comparison tool ---------- */
  const cache = new Map();
  const loadColumn = async (item) => {
    if (cache.has(item.h)) return cache.get(item.h);
    const url = `${cfg.root.replace(/\/$/, '')}/products/${encodeURIComponent(item.h)}?section_id=compare-column`;
    const res = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error(String(res.status));
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const col = qs('[data-cmp-column]', doc);
    if (!col) throw new Error('column');
    cache.set(item.h, col);
    return col;
  };

  let building = 0;
  const buildTool = async () => {
    const tool = qs('[data-compare-tool]');
    if (!tool) return;
    const run = ++building;
    const params = new URLSearchParams(window.location.search);
    let list = read();
    if (params.get('p')) {
      const shared = params.get('p').split(',').filter(Boolean).slice(0, MAX);
      list = shared.map((h) => list.find((x) => x.h === h) || { h, t: h, i: '', u: `${cfg.root.replace(/\/$/, '')}/products/${h}` });
      write(list);
      params.delete('p');
      const clean = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', clean);
    }
    const empty = qs('[data-cmp-empty]', tool);
    const skeleton = qs('template[data-cmp-skeleton]', tool);
    const mount = qs('[data-cmp-mount]', tool);
    const loading = qs('[data-cmp-loading]', tool);
    mount.replaceChildren();
    empty.hidden = list.length >= 2;
    qs('[data-cmp-one]', tool).hidden = list.length !== 1;
    if (list.length < 2) return;
    loading.hidden = false;
    const results = await Promise.allSettled(list.map(loadColumn));
    if (run !== building) return;
    loading.hidden = true;
    const ok = [];
    let stored = read();
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        ok.push(r.value);
        // Names, links and images in the visitor's language, also for a shared link
        const d = r.value.dataset;
        stored = stored.map((x) => (x.h === list[i].h ? { h: x.h, t: d.title, i: d.image, u: d.url } : x));
      } else if (r.reason?.message === '404') {
        stored = stored.filter((x) => x.h !== list[i].h);
      }
    });
    write(stored);
    if (ok.length < 2) { empty.hidden = false; return; }

    const wrap = skeleton.content.firstElementChild.cloneNode(true);
    const n = ok.length;
    wrap.dataset.cols = String(n);
    const headRow = qs('.cmp__head', wrap);
    const footRow = qs('.cmp__foot', wrap);
    ok.forEach((col, idx) => {
      const c = String(idx + 1);
      const head = qs('[data-part="head"] th', col).cloneNode(true);
      head.dataset.col = c;
      headRow.append(head);
      qsa('.cmp__row', wrap).forEach((row) => {
        const cell = qs(`table[data-key="${row.dataset.key}"] td`, col);
        const td = cell ? cell.cloneNode(true) : document.createElement('td');
        td.dataset.col = c;
        row.append(td);
      });
      const foot = qs('[data-part="foot"] td', col).cloneNode(true);
      foot.dataset.col = c;
      footRow.append(foot);
    });
    // Same rules as the server tables: rows without any value are left out, differing rows marked.
    qsa('.cmp__row', wrap).forEach((row) => {
      const values = qsa('td', row).map((td) => (qs('.cmp__na', td) ? '' : td.textContent.replace(/\s+/g, ' ').trim()));
      if (values.every((v) => v === '')) { row.remove(); return; }
      if (!values.every((v) => v === values[0])) row.classList.add('is-diff');
    });
    qsa('.cmp__group', wrap).forEach((g) => {
      if (!qs('.cmp__row', g)) g.remove();
      else qs('.cmp__group-row th', g).colSpan = n + 1;
    });
    mount.append(wrap);
    setupTable(wrap);
    const share = qs('[data-cmp-share]', tool);
    if (share) {
      const url = new URL(window.location.href);
      url.searchParams.set('p', read().map((x) => x.h).join(','));
      share.dataset.url = url.toString();
      share.hidden = false;
    }
    announce(fill(cfg.strings.loaded, { COUNT: n }));
  };

  document.addEventListener('click', async (event) => {
    const share = event.target.closest('[data-cmp-share]');
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.dataset.url);
      announce(cfg.strings.copied);
    } catch (e) {
      window.prompt(cfg.strings.copy_prompt, share.dataset.url);
    }
  });

  const init = () => {
    render();
    qsa('[data-cmp]').forEach(setupTable);
    buildTool();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  // Sections re-rendered by the theme (cart drawer, recommendations) bring new buttons; only their
  // aria-pressed is updated here (attributes are not observed, so this cannot loop).
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => { queued = false; syncButtons(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
